import { useEffect } from "react";
import Layout from "../components/layout/Layout";
import { injectHeroDotWave } from "../lib/heroDotWave";
import { Link } from "react-router-dom";

/* ------------------------------------------------------------------ */
/*  Mock content — pulled verbatim from the original index.html        */
/* ------------------------------------------------------------------ */

const HERO_KPIS = [
  { label: "Orders Today", value: "487" },
  { label: "Revenue MTD", value: "₹92.4k" },
  { label: "Active SKUs", value: "1,340" },
  { label: "Pending", value: "₹7.2k" },
];

const HERO_BARS = [
  { label: "Mon", height: "45%" },
  { label: "Tue", height: "72%" },
  { label: "Wed", height: "58%" },
  { label: "Thu", height: "85%" },
  { label: "Fri", height: "65%" },
  { label: "Sat", height: "90%" },
  { label: "Sun", height: "40%" },
];

type OrderStatus = "confirmed" | "processing" | "dispatched";
const HERO_RECENT_ORDERS: Array<{
  id: string;
  name: string;
  status: OrderStatus;
  label: string;
}> = [
  {
    id: "#ORD-1847",
    name: "Horizon Distributors",
    status: "confirmed",
    label: "Confirmed",
  },
  {
    id: "#ORD-1846",
    name: "Alto Brewing Co.",
    status: "processing",
    label: "Processing",
  },
  {
    id: "#ORD-1845",
    name: "Sahara Beverages",
    status: "dispatched",
    label: "Dispatched",
  },
  {
    id: "#ORD-1844",
    name: "Pinnacle Spirits",
    status: "confirmed",
    label: "Confirmed",
  },
];

const PAIN_CARDS = [
  {
    img: "orders-lost",
    alt: "Orders lost in translation",
    title: "Orders lost in translation",
    body: "Verbal orders. Missed items. No confirmation trail.",
  },
  {
    img: "inventory",
    alt: "Inventory is a guessing game",
    title: "Inventory is a guessing game",
    body: "Stock levels live in someone's head, not your system.",
  },
  {
    img: "payments",
    alt: "Payments tracked manually",
    title: "Payments tracked manually",
    body: "Chasing invoices. Unclear credit terms. No single view.",
  },
  {
    img: "zero-real-time",
    alt: "Zero real-time visibility",
    title: "Zero real-time visibility",
    body: "No dashboard. No alerts. Decisions on gut feel.",
  },
];

const ABOUT_AUDIENCE: Array<{
  img: string;
  iconClass: string;
  name: string;
  desc: string;
}> = [
  {
    img: "buyers",
    iconClass: "icon-box-orange",
    name: "Buyers & Sellers",
    desc: "Self-serve ordering, no calls needed",
  },
  {
    img: "ops",
    iconClass: "icon-box-teal",
    name: "Operations Teams",
    desc: "Inventory, dispatch & order control",
  },
  {
    img: "sales-rep",
    iconClass: "icon-box-green",
    name: "Sales Reps",
    desc: "Outlet tracking & performance visibility",
  },
  {
    img: "finance",
    iconClass: "icon-box-teal",
    name: "Finance",
    desc: "Payments, credit & invoice clarity",
  },
];

const RESULTS = [
  { count: "4.8/5", display: "4.8/5", label: "Average customer rating" },
  { count: "42%", display: "42%", label: "Reduction in follow-ups" },
  { count: "3x", display: "3x", label: "Faster order processing" },
  { count: "96%", display: "96%", label: "Order accuracy rate" },
];

const TESTIMONIALS = [
  {
    initials: "RM",
    avatarColor: "#1F5F5B",
    quote:
      '"Maltora replaced three different tools we were juggling. Our order errors dropped to near zero within the first month. The team actually enjoys using it."',
    name: "Rajesh Menon",
    role: "Operations Head, Mirage",
  },
  {
    initials: "PS",
    avatarColor: "#5F8F3A",
    quote:
      '"We used to spend half the morning reconciling orders over WhatsApp. Now everything flows through Maltora and our reps can focus on what matters: selling."',
    name: "Priya Sharma",
    role: "Sales Director, Social",
  },
  {
    initials: "AP",
    avatarColor: "#0B4740",
    quote:
      '"The payment tracking alone saved us lakhs in revenue leakage. We finally have visibility into credit cycles and can make decisions based on real data."',
    name: "Amit Patel",
    role: "CFO, Big Bull",
  },
];

/* ------------------------------------------------------------------ */
/*  Tiny inline icon helpers — kept here to keep JSX terse              */
/* ------------------------------------------------------------------ */
const scrollToCTA = (e: React.MouseEvent<HTMLAnchorElement>) => {
  e.preventDefault();

  const target = document.getElementById("cta");
  if (!target) return;

  target.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
};
const StarIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="#F59E0B"
    stroke="#F59E0B"
    strokeWidth="1"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const CheckIcon = ({ stroke }: { stroke: string }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke={stroke}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function HomePage() {
  // The hero dot-wave is procedurally generated in the original site; port it.
  useEffect(() => {
    injectHeroDotWave();
  }, []);

  const scrollToHowItWorks = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    const target = document.getElementById("how-it-works");
    if (!target) return;

    target.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <Layout>
      {/* ============== 1. HERO ============== */}
      <section className="hero-section" id="hero">
        <div className="section-inner">
          <div className="hero-inner">
            <div className="hero-content">
              <h1 className="hero-title">
                Your brewery.
                <br />
                <em className="italic-accent">Finally</em> running
                <br />
                like a system.
              </h1>
              <p className="hero-body">
                Maltora replaces the spreadsheets, WhatsApp threads, and siloed
                tools holding your operation back. One platform for orders,
                inventory, payments, and every person in the chain.
              </p>
              <div className="hero-ctas">
                <Link
                  to="/signup"
                  state={{ fromApp: true }}
                  className="btn btn-primary"
                >
                  Get started
                </Link>

                <a
                  href="#how-it-works"
                  className="btn btn-ghost-dark"
                  onClick={scrollToHowItWorks}
                >
                  See how it works &rarr;
                </a>
              </div>
            </div>
            <div className="hero-visual">
              <div className="product-composition">
                <span className="sparkle sparkle-1">&#10022;</span>
                <span className="sparkle sparkle-2">&#10022;</span>

                <div className="floating-card floating-top-right anim-card">
                  <div className="icon-box icon-box-orange">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                      <polyline points="17 6 23 6 23 12" />
                    </svg>
                  </div>
                  <div className="floating-card-text">
                    <strong>42% fewer follow-ups</strong>
                    <span>This week vs last</span>
                  </div>
                </div>

                <div className="floating-card floating-bottom-left anim-card">
                  <div className="icon-box icon-box-teal">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <div className="floating-card-text">
                    <strong>Order confirmed</strong>
                    <span>Alto Brewing &middot; 2 min ago</span>
                  </div>
                </div>

                <div className="browser-frame browser-frame-teal">
                  <div className="browser-chrome">
                    <div className="browser-dots">
                      <span className="dot dot-red"></span>
                      <span className="dot dot-yellow"></span>
                      <span className="dot dot-green"></span>
                    </div>
                    <div className="browser-url">maltora.app/dashboard</div>
                  </div>
                  <div className="browser-body">
                    <div className="kpi-grid">
                      {HERO_KPIS.map((k) => (
                        <div className="kpi-card" key={k.label}>
                          <span className="kpi-label">{k.label}</span>
                          <span className="kpi-value">{k.value}</span>
                        </div>
                      ))}
                    </div>
                    <div className="dashboard-content">
                      <div className="chart-area">
                        <div className="chart-title">Weekly Orders</div>
                        <div className="bar-chart">
                          {HERO_BARS.map((b) => (
                            <div
                              className="bar"
                              style={{ height: b.height }}
                              key={b.label}
                            >
                              <span>{b.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="recent-orders">
                        <div className="orders-title">Recent Orders</div>
                        {HERO_RECENT_ORDERS.map((o) => (
                          <div className="order-row" key={o.id}>
                            <span className="order-id">{o.id}</span>
                            <span className="order-name">{o.name}</span>
                            <span className={`order-status status-${o.status}`}>
                              {o.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== 2. PROBLEM / STATS ============== */}
      <section className="section white section-reveal" id="problem">
        <div className="section-inner">
          <div className="section-header" style={{ textAlign: "left" }}>
            <span className="overline">THE PROBLEM</span>
            <h2>Manual coordination doesn&apos;t scale.</h2>
            <p className="section-sub" style={{ maxWidth: 640, marginLeft: 0 }}>
              Running a brewery on WhatsApp and gut feel doesn&apos;t just cause
              headaches. It eats into your margin. Businesses using manual
              workflows see:
            </p>
            <ul
              style={{
                listStyle: "disc",
                paddingLeft: 20,
                margin: "16px 0 0",
                maxWidth: 720,
                color: "#64748B",
                fontSize: 15,
                lineHeight: 1.7,
              }}
            >
              <li>
                Every sales rep spends nearly half their week on logistics
                instead of selling.
              </li>
              <li>
                Untracked credits and unbilled &ldquo;samples&rdquo; or
                deliveries drain monthly revenue.
              </li>
              <li>
                The 24-hour delay is the time between a customer wanting a
                beverage and the warehouse knowing about it.
              </li>
            </ul>
          </div>
          <div
            style={{
              marginTop: 48,
              background: "#F1F1EC",
              borderRadius: 16,
              padding: "40px 24px",
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 16,
            }}
          >
            {[
              {
                value: "500",
                label: "Outlets managed without adding a single Ops hire.",
              },
              {
                value: "100%",
                label: "Capture of unbilled deliveries, instantly.",
              },
              {
                value: "15+",
                label: "Hours recovered per rep, every week, for selling.",
              },
              {
                value: "92%",
                label: "Of orders processed 'touchless' to the warehouse.",
              },
            ].map((s, i) => (
              <div
                key={s.value}
                style={{
                  textAlign: "center",
                  padding: "0 16px",
                  borderRight: i < 3 ? "1px solid rgba(15,23,42,0.08)" : "none",
                }}
              >
                <div
                  style={{
                    fontFamily:
                      "var(--font-display, Satoshi, DM Sans, system-ui, sans-serif)",
                    fontSize: 44,
                    fontWeight: 700,
                    color: "#0F172A",
                    lineHeight: 1.1,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {s.value}
                </div>
                <div
                  style={{
                    marginTop: 12,
                    fontSize: 13,
                    color: "#64748B",
                    lineHeight: 1.5,
                  }}
                >
                  {s.label}
                </div>
              </div>
            ))}
          </div>
          <div style={{ height: 48 }} />
          <div className="pain-grid">
            {PAIN_CARDS.map((p) => (
              <div className="pain-card anim-card" key={p.title}>
                <div
                  className="icon-box icon-box-neutral"
                  style={{ width: 50, height: 50 }}
                >
                  <img
                    src={`/landing/images/${p.img}.png`}
                    alt={p.alt}
                    style={{ width: 32, height: 32, objectFit: "contain" }}
                  />
                </div>
                <h3>{p.title}</h3>
                <p>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== 3. ABOUT ============== */}
      <section className="section white section-reveal" id="about">
        <div className="section-inner">
          <div className="about-grid">
            <div className="about-content">
              <span className="overline">ABOUT MALTORA</span>
              <h2>
                The operating system for{" "}
                <em className="italic-accent">beverage distribution.</em>
              </h2>
              <p className="about-body">
                Maltora is built for breweries that are growing beyond manual
                ways of working. As order volumes grow, calls, spreadsheets,
                disconnected updates, and manual follow-ups start slowing
                everything down.
              </p>
              <p className="about-body">
                Maltora brings ordering, inventory, payments, and execution into
                one connected system so teams can run distribution with more
                clarity and control.
              </p>
            </div>
            <div className="about-aside">
              <p className="about-built-label">
                Built for every part of the workflow
              </p>
              <div className="about-audience">
                {ABOUT_AUDIENCE.map((a) => (
                  <div className="about-audience-item" key={a.name}>
                    <div className={`icon-box ${a.iconClass}`}>
                      <img
                        src={`/landing/images/${a.img}.png`}
                        alt={a.name}
                        style={{ width: 28, height: 28, objectFit: "contain" }}
                      />
                    </div>
                    <div>
                      <div className="about-audience-name">{a.name}</div>
                      <div className="about-audience-desc">{a.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== 4. PLATFORM (sticky stack) ============== */}
      <section className="section" id="features">
        <div className="section-inner">
          <div className="section-header center">
            <span className="overline">PLATFORM</span>
            <h2>
              One platform for{" "}
              <span className="underline-word">
                every
                <svg
                  className="squiggle"
                  viewBox="0 0 100 12"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M0 8 Q 25 0, 50 8 Q 75 16, 100 8"
                    fill="none"
                    stroke="#0D9488"
                    strokeWidth="2.5"
                  />
                </svg>
              </span>{" "}
              stakeholder.
            </h2>
            <p className="section-sub">
              From the brewery floor to the buyer&apos;s phone, Maltora connects
              every person in your supply chain.
            </p>
          </div>
          <div className="sticky-stack">
            {/* Card 1: Orders & Dispatch */}
            <div
              className="stack-card sticky-card"
              style={{ ["--card-index" as never]: 0 }}
            >
              <div className="stack-card-content">
                <div className="stack-card-left">
                  <div className="icon-box icon-box-teal">
                    <img
                      src="/landing/images/order.png"
                      alt="Orders & Dispatch"
                      style={{ width: 28, height: 28, objectFit: "contain" }}
                    />
                  </div>
                  <h3>Orders &amp; Dispatch</h3>
                  <p>
                    Replace phone calls and WhatsApp with structured digital
                    orders. Buyers order from a catalog, sellers confirm with a
                    click, and dispatch is tracked end-to-end.
                  </p>
                </div>
                <div className="stack-card-right tint-teal">
                  <div className="browser-frame browser-frame-teal browser-small">
                    <div className="browser-chrome">
                      <div className="browser-dots">
                        <span className="dot dot-red"></span>
                        <span className="dot dot-yellow"></span>
                        <span className="dot dot-green"></span>
                      </div>
                      <div className="browser-url">maltora.app/orders</div>
                    </div>
                    <div className="browser-body">
                      <div className="mock-table">
                        <div className="mock-row mock-header">
                          <span>Order ID</span>
                          <span>Buyer</span>
                          <span>Items</span>
                          <span>Status</span>
                        </div>
                        <div className="mock-row">
                          <span>#1847</span>
                          <span>Horizon Dist.</span>
                          <span>24 cases</span>
                          <span className="tag tag-green">Confirmed</span>
                        </div>
                        <div className="mock-row">
                          <span>#1846</span>
                          <span>Alto Brewing</span>
                          <span>18 cases</span>
                          <span className="tag tag-yellow">Processing</span>
                        </div>
                        <div className="mock-row">
                          <span>#1845</span>
                          <span>Sahara Bev.</span>
                          <span>36 cases</span>
                          <span className="tag tag-blue">Dispatched</span>
                        </div>
                        <div className="mock-row">
                          <span>#1844</span>
                          <span>Pinnacle Spirits</span>
                          <span>12 cases</span>
                          <span className="tag tag-green">Confirmed</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Inventory & Stock */}
            <div
              className="stack-card sticky-card"
              style={{ ["--card-index" as never]: 1 }}
            >
              <div className="stack-card-content">
                <div className="stack-card-left">
                  <div className="icon-box icon-box-navy">
                    <img
                      src="/landing/images/stock.png"
                      alt="Inventory & Stock"
                      style={{ width: 28, height: 28, objectFit: "contain" }}
                    />
                  </div>
                  <h3>Inventory &amp; Stock</h3>
                  <p>
                    Track every SKU across warehouses in real time. Get alerts
                    when stock runs low, and sync inventory with incoming orders
                    automatically.
                  </p>
                </div>
                <div className="stack-card-right tint-navy">
                  <div className="browser-frame browser-frame-navy browser-small">
                    <div className="browser-chrome">
                      <div className="browser-dots">
                        <span className="dot dot-red"></span>
                        <span className="dot dot-yellow"></span>
                        <span className="dot dot-green"></span>
                      </div>
                      <div className="browser-url">maltora.app/inventory</div>
                    </div>
                    <div className="browser-body">
                      <div className="mock-table">
                        <div className="mock-row mock-header">
                          <span>SKU</span>
                          <span>Product</span>
                          <span>Stock</span>
                          <span>Status</span>
                        </div>
                        <div className="mock-row">
                          <span>ML-001</span>
                          <span>Kingfisher Premium</span>
                          <span>2,450</span>
                          <span className="tag tag-green">In Stock</span>
                        </div>
                        <div className="mock-row">
                          <span>ML-002</span>
                          <span>Tuborg Strong</span>
                          <span>180</span>
                          <span className="tag tag-yellow">Low</span>
                        </div>
                        <div className="mock-row">
                          <span>ML-003</span>
                          <span>Budweiser Magnum</span>
                          <span>1,890</span>
                          <span className="tag tag-green">In Stock</span>
                        </div>
                        <div className="mock-row">
                          <span>ML-004</span>
                          <span>Carlsberg Elephant</span>
                          <span>42</span>
                          <span className="tag tag-red">Critical</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 3: Payments & Credit */}
            <div
              className="stack-card sticky-card"
              style={{ ["--card-index" as never]: 2 }}
            >
              <div className="stack-card-content">
                <div className="stack-card-left">
                  <div className="icon-box icon-box-green">
                    <img
                      src="/landing/images/credit.png"
                      alt="Payments & Credit"
                      style={{ width: 28, height: 28, objectFit: "contain" }}
                    />
                  </div>
                  <h3>Payments &amp; Credit</h3>
                  <p>
                    See every rupee owed and received. Automate payment
                    reminders, manage credit limits per buyer, and reconcile
                    invoices without manual effort.
                  </p>
                </div>
                <div className="stack-card-right tint-green">
                  <div className="browser-frame browser-frame-green browser-small">
                    <div className="browser-chrome">
                      <div className="browser-dots">
                        <span className="dot dot-red"></span>
                        <span className="dot dot-yellow"></span>
                        <span className="dot dot-green"></span>
                      </div>
                      <div className="browser-url">maltora.app/payments</div>
                    </div>
                    <div className="browser-body">
                      <div className="mock-table">
                        <div className="mock-row mock-header">
                          <span>Buyer</span>
                          <span>Outstanding</span>
                          <span>Credit</span>
                          <span>Status</span>
                        </div>
                        <div className="mock-row">
                          <span>Horizon Dist.</span>
                          <span>&#8377;1,24,000</span>
                          <span>&#8377;2,00,000</span>
                          <span className="tag tag-green">On Track</span>
                        </div>
                        <div className="mock-row">
                          <span>Alto Brewing</span>
                          <span>&#8377;87,400</span>
                          <span>&#8377;1,00,000</span>
                          <span className="tag tag-yellow">Near Limit</span>
                        </div>
                        <div className="mock-row">
                          <span>Sahara Bev.</span>
                          <span>&#8377;2,12,000</span>
                          <span>&#8377;2,00,000</span>
                          <span className="tag tag-red">Over Limit</span>
                        </div>
                        <div className="mock-row">
                          <span>Pinnacle Spirits</span>
                          <span>&#8377;45,600</span>
                          <span>&#8377;1,50,000</span>
                          <span className="tag tag-green">On Track</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== 5. PORTALS ============== */}
      <section className="section white" id="portals">
        <div className="section-inner">
          <div className="section-header center">
            <span className="overline">PORTALS</span>
            <h2>
              Built for{" "}
              <em className="italic-accent">
                every
                <svg
                  className="squiggle"
                  viewBox="0 0 100 12"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M0 8 Q 25 0, 50 8 Q 75 16, 100 8"
                    fill="none"
                    stroke="#E86A0D"
                    strokeWidth="2.5"
                  />
                </svg>
              </em>{" "}
              stakeholder.
            </h2>
            <p className="section-sub">
              Each person in your supply chain gets a dedicated portal, tailored
              to how they work.
            </p>
          </div>

          <div className="stack-cards">
            {/* Customer Portal */}
            <div className="stack-card anim-card">
              <div className="stack-card-content">
                <div className="stack-card-left">
                  <div className="icon-box icon-box-blue">
                    <img
                      src="/landing/images/customer-portal.png"
                      width="32"
                      height="32"
                      alt="Customer Portal"
                      style={{ objectFit: "contain" }}
                    />
                  </div>
                  <span className="portal-overline portal-overline-blue">
                    CUSTOMER PORTAL
                  </span>
                  <h3>
                    Order with confidence.
                    <br />
                    <em className="italic-accent">No calls required.</em>
                  </h3>
                  <p>
                    Your buyers get a clean self-serve portal to browse your
                    catalog, place bulk orders, track deliveries, and manage
                    their account.
                  </p>
                  <ul className="portal-checklist">
                    <li>
                      <CheckIcon stroke="#93C5FD" />
                      Browse live catalog with real-time stock availability
                    </li>
                    <li>
                      <CheckIcon stroke="#93C5FD" />
                      Place and re-order in under 2 minutes
                    </li>
                    <li>
                      <CheckIcon stroke="#93C5FD" />
                      Track order status from confirmation to delivery
                    </li>
                    <li>
                      <CheckIcon stroke="#93C5FD" />
                      View invoice history and payment status
                    </li>
                    <li>
                      <CheckIcon stroke="#93C5FD" />
                      Pre-book limited seasonal releases before they sell out
                    </li>
                  </ul>
                </div>
                <div className="stack-card-right tint-orange">
                  <div className="browser-frame browser-frame-black browser-small">
                    <div className="browser-chrome">
                      <div className="browser-dots">
                        <span className="dot dot-red"></span>
                        <span className="dot dot-yellow"></span>
                        <span className="dot dot-green"></span>
                      </div>
                      <div className="browser-url">
                        customer.maltora.app &middot; Place Order
                      </div>
                    </div>
                    <div className="browser-body">
                      <div className="p-order-header">
                        <strong>New Order</strong>
                        <span className="tag tag-customer-items">3 Items</span>
                      </div>
                      <div className="p-product">
                        <div>
                          <div className="p-name">Bangalore Daze IPA</div>
                          <div className="p-sub">
                            Craft Draught &middot; 30L Keg &middot; &#8377;4,200
                          </div>
                        </div>
                        <div className="p-qty">
                          <button>&minus;</button>
                          <span>4</span>
                          <button>+</button>
                        </div>
                      </div>
                      <div className="p-product">
                        <div>
                          <div className="p-name">Golden Harvest Lager</div>
                          <div className="p-sub">
                            Craft Draught &middot; 30L Keg &middot; &#8377;3,800
                          </div>
                        </div>
                        <div className="p-qty">
                          <button>&minus;</button>
                          <span>2</span>
                          <button>+</button>
                        </div>
                      </div>
                      <div className="p-product">
                        <div>
                          <div className="p-name">Arm Strong Ale BGSA</div>
                          <div className="p-sub">
                            Craft Draught &middot; 30L Keg &middot; &#8377;4,600
                          </div>
                        </div>
                        <div className="p-qty">
                          <button>&minus;</button>
                          <span>3</span>
                          <button>+</button>
                        </div>
                      </div>
                      <div className="p-total">
                        <span>Order Total</span>
                        <strong>&#8377;43,400</strong>
                      </div>
                      <div className="p-place-btn">Place Order &rarr;</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Seller Portal */}
            <div className="stack-card anim-card">
              <div className="stack-card-content">
                <div className="stack-card-left">
                  <div className="icon-box icon-box-teal">
                    <img
                      src="/landing/images/seller.png"
                      width="32"
                      height="32"
                      alt="Seller Portal"
                      style={{ objectFit: "contain" }}
                    />
                  </div>
                  <span className="portal-overline portal-overline-teal">
                    SELLER PORTAL
                  </span>
                  <h3>
                    Run your brewery.
                    <br />
                    <em className="italic-accent">Not your inbox.</em>
                  </h3>
                  <p>
                    Your operations team gets a full command centre: live
                    inventory, incoming orders, customer records, dispatch
                    tracking, and finance analytics.
                  </p>
                  <ul className="portal-checklist">
                    <li>
                      <CheckIcon stroke="#1F5F5B" />
                      Real-time inventory across all product SKUs
                    </li>
                    <li>
                      <CheckIcon stroke="#1F5F5B" />
                      Approve, adjust, and dispatch orders in one flow
                    </li>
                    <li>
                      <CheckIcon stroke="#1F5F5B" />
                      Onboard new customers with GST and license verification
                    </li>
                    <li>
                      <CheckIcon stroke="#1F5F5B" />
                      Finance dashboard: outstanding ageing and payment cycles
                    </li>
                    <li>
                      <CheckIcon stroke="#1F5F5B" />
                      Generate excise and service invoices automatically
                    </li>
                  </ul>
                </div>
                <div className="stack-card-right tint-teal">
                  <div className="browser-frame browser-frame-teal browser-small">
                    <div className="browser-chrome">
                      <div className="browser-dots">
                        <span className="dot dot-red"></span>
                        <span className="dot dot-yellow"></span>
                        <span className="dot dot-green"></span>
                      </div>
                      <div className="browser-url">
                        seller.maltora.app &middot; Inventory
                      </div>
                    </div>
                    <div className="browser-body">
                      <div className="p-kpi-row">
                        <div className="p-kpi">
                          <div className="p-kpi-val">487</div>
                          <div className="p-kpi-lbl">Orders / Mo</div>
                          <div className="p-kpi-delta p-delta-up">
                            &uarr; 12%
                          </div>
                        </div>
                        <div className="p-kpi">
                          <div className="p-kpi-val">&#8377;8.4L</div>
                          <div className="p-kpi-lbl">Revenue MTD</div>
                          <div className="p-kpi-delta p-delta-up">
                            &uarr; 9%
                          </div>
                        </div>
                        <div className="p-kpi">
                          <div className="p-kpi-val">3</div>
                          <div className="p-kpi-lbl">Low Stock</div>
                          <div className="p-kpi-delta p-delta-warn">
                            &#9888; Action
                          </div>
                        </div>
                      </div>
                      <div className="mock-table">
                        <div className="mock-row mock-header">
                          <span>Product</span>
                          <span>Kegs</span>
                          <span>Reserved</span>
                          <span>Status</span>
                        </div>
                        <div className="mock-row">
                          <span>Bangalore Daze IPA</span>
                          <span>19</span>
                          <span>0</span>
                          <span className="tag tag-green">In Stock</span>
                        </div>
                        <div className="mock-row">
                          <span>Golden Harvest Lager</span>
                          <span>1</span>
                          <span>14</span>
                          <span className="tag tag-red">Critical</span>
                        </div>
                        <div className="mock-row">
                          <span>Arm Strong Ale BGSA</span>
                          <span>52</span>
                          <span>3</span>
                          <span className="tag tag-green">In Stock</span>
                        </div>
                        <div className="mock-row">
                          <span>Soul Surfer Hefeweizen</span>
                          <span>5</span>
                          <span>8</span>
                          <span className="tag tag-yellow">Low</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sales Rep Portal */}
            <div className="stack-card anim-card">
              <div className="stack-card-content">
                <div className="stack-card-left">
                  <div className="icon-box icon-box-green">
                    <img
                      src="/landing/images/sales-rep.png"
                      width="32"
                      height="32"
                      alt="Sales Rep Portal"
                      style={{ objectFit: "contain" }}
                    />
                  </div>
                  <span className="portal-overline portal-overline-green">
                    SALES REP PORTAL
                  </span>
                  <h3>
                    More time selling.
                    <br />
                    <em className="italic-accent">Zero coordination tax.</em>
                  </h3>
                  <p>
                    Sales reps get their own focused view: assigned outlets,
                    visit history, performance targets, and real-time order
                    visibility.
                  </p>
                  <ul className="portal-checklist">
                    <li>
                      <CheckIcon stroke="#5F8F3A" />
                      See all assigned outlets and visit history at a glance
                    </li>
                    <li>
                      <CheckIcon stroke="#5F8F3A" />
                      Track monthly sales target with live progress
                    </li>
                    <li>
                      <CheckIcon stroke="#5F8F3A" />
                      Flag lapsed accounts and re-engage before they churn
                    </li>
                    <li>
                      <CheckIcon stroke="#5F8F3A" />
                      View orders placed by their outlets in real time
                    </li>
                    <li>
                      <CheckIcon stroke="#5F8F3A" />
                      Compare performance across time periods
                    </li>
                  </ul>
                </div>
                <div className="stack-card-right tint-green">
                  <div className="browser-frame browser-frame-green browser-small">
                    <div className="browser-chrome">
                      <div className="browser-dots">
                        <span className="dot dot-red"></span>
                        <span className="dot dot-yellow"></span>
                        <span className="dot dot-green"></span>
                      </div>
                      <div className="browser-url">
                        salesrep.maltora.app &middot; Dashboard
                      </div>
                    </div>
                    <div className="browser-body">
                      <div className="p-rep-header">
                        <div className="p-rep-avatar">AR</div>
                        <div>
                          <div className="p-rep-name">John</div>
                          <div className="p-rep-sub">
                            Bangalore &middot; 14 outlets
                          </div>
                        </div>
                        <span
                          className="tag tag-green"
                          style={{ marginLeft: "auto" }}
                        >
                          Active
                        </span>
                      </div>
                      <div className="p-target">
                        <div className="p-target-bar">
                          <div className="p-target-fill"></div>
                        </div>
                        <div className="p-target-info">
                          <span>Monthly Target &middot; 70%</span>
                          <span>&#8377;2.1L / &#8377;3L</span>
                        </div>
                      </div>
                      <div className="p-outlet">
                        <div>
                          <div className="p-outlet-name">The Rooftop Grove</div>
                          <div className="p-outlet-last">
                            3 days ago &middot; &#8377;24,600
                          </div>
                        </div>
                        <button className="p-visit-btn">Visit</button>
                      </div>
                      <div className="p-outlet">
                        <div>
                          <div className="p-outlet-name">
                            Pinecrest Residence
                          </div>
                          <div className="p-outlet-last">
                            11 days ago &middot; &#8377;12,000
                          </div>
                        </div>
                        <button className="p-visit-btn">Visit</button>
                      </div>
                      <div className="p-outlet">
                        <div>
                          <div className="p-outlet-name p-lapsed">
                            Metro Tavern
                          </div>
                          <div className="p-outlet-last p-lapsed">
                            Lapsed &middot; 34 days since last order
                          </div>
                        </div>
                        <button className="p-visit-btn p-visit-lapsed">
                          Re-engage
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== 6. HOW IT WORKS ============== */}
      <section className="section section-reveal dark" id="how-it-works">
        <div className="section-inner">
          <div className="section-header">
            <span className="overline">HOW IT WORKS</span>
            <h2>
              One flow. <em className="italic-accent">Every</em> stakeholder.
            </h2>
            <p className="section-sub">
              Four steps from order to fulfillment. No spreadsheets, no phone
              calls.
            </p>
          </div>
          <div className="how-grid">
            <div className="how-steps">
              {[
                {
                  n: 1,
                  title: "Buyers place orders digitally",
                  body: "No more phone calls or scribbled notes. Buyers browse a live catalog, select quantities, and submit orders from their phone in under a minute.",
                },
                {
                  n: 2,
                  title: "Sellers confirm with live context",
                  body: "See live inventory, outstanding dues, and order history before confirming. One click to accept, modify, or flag an order for review.",
                },
                {
                  n: 3,
                  title: "Reps stay focused on selling",
                  body: "Route plans, targets, and account insights on one screen. No time wasted chasing order statuses or payment updates.",
                },
                {
                  n: 4,
                  title: "Finance closes faster",
                  body: "Auto-reconciled payments, real-time receivables, and clean books. Month-end goes from days to hours.",
                },
              ].map((s) => (
                <div className="how-step anim-card" key={s.n}>
                  <span className="step-badge">{s.n}</span>
                  <div className="step-content">
                    <h3>{s.title}</h3>
                    <p>{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="how-visual">
              <div className="notification-stack">
                <div className="notif-card anim-card">
                  <div className="icon-box icon-box-teal icon-box-sm">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <path d="M16 10a4 4 0 0 1-8 0" />
                    </svg>
                  </div>
                  <div className="notif-text">
                    <strong>New order received</strong>
                    <span>
                      Horizon Distributors &middot; 24 cases &middot; just now
                    </span>
                  </div>
                </div>
                <div className="notif-card anim-card">
                  <div className="icon-box icon-box-green icon-box-sm">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <div className="notif-text">
                    <strong>Order #1846 confirmed</strong>
                    <span>
                      Alto Brewing Co. &middot; 18 cases &middot; 2 min ago
                    </span>
                  </div>
                </div>
                <div className="notif-card anim-card">
                  <div className="icon-box icon-box-orange icon-box-sm">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="1" y="3" width="15" height="13" />
                      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                      <circle cx="5.5" cy="18.5" r="2.5" />
                      <circle cx="18.5" cy="18.5" r="2.5" />
                    </svg>
                  </div>
                  <div className="notif-text">
                    <strong>Dispatch initiated</strong>
                    <span>
                      Sahara Beverages &middot; 36 cases &middot; 5 min ago
                    </span>
                  </div>
                </div>
                <div className="notif-card anim-card">
                  <div className="icon-box icon-box-teal icon-box-sm">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="12" y1="1" x2="12" y2="23" />
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                    </svg>
                  </div>
                  <div className="notif-text">
                    <strong>Payment received</strong>
                    <span>
                      Pinnacle Spirits &middot; &#8377;45,600 &middot; 12 min
                      ago
                    </span>
                  </div>
                </div>
                <div className="notif-card anim-card">
                  <div className="icon-box icon-box-neutral icon-box-sm">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  </div>
                  <div className="notif-text">
                    <strong>Low stock alert</strong>
                    <span>
                      Tuborg Strong &middot; 180 units remaining &middot; 18 min
                      ago
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== 7. RESULTS / TESTIMONIALS ============== */}
      <section className="section white section-reveal" id="results">
        <div className="section-inner">
          <div className="section-header center">
            <span className="overline">RESULTS</span>
            <h2>
              Numbers from breweries{" "}
              <em className="italic-accent">like yours.</em>
            </h2>
          </div>
          <div className="results-row">
            {RESULTS.map((r) => (
              <div className="result-item anim-card" key={r.label}>
                <span className="result-number" data-count={r.count}>
                  {r.display}
                </span>
                <span className="result-label">{r.label}</span>
              </div>
            ))}
          </div>
          <div className="testimonials-grid">
            {TESTIMONIALS.map((t) => (
              <div className="testimonial-card anim-card" key={t.name}>
                <div className="stars">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <StarIcon key={i} />
                  ))}
                </div>
                <p className="testimonial-quote">{t.quote}</p>
                <div className="testimonial-author">
                  <div
                    className="testimonial-avatar"
                    style={{ background: t.avatarColor }}
                  >
                    {t.initials}
                  </div>
                  <div>
                    <strong>{t.name}</strong>
                    <span>{t.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== 8. CTA BAND ============== */}
      <section className="section dark section-reveal" id="cta">
        <div className="section-inner center">
          <span className="overline accent">GET IN TOUCH</span>
          <h2>
            Ready to run a tighter
            <br />
            <em className="italic-accent">operation?</em>
          </h2>
          <p className="section-sub muted-cream">
            Join the beverage businesses already streamlining orders, inventory,
            and payments with Maltora.
          </p>
          <div className="cta-pair">
            <Link
              to="/contact"
              state={{ fromApp: true }}
              className="btn btn-accent"
            >
              Get in touch
            </Link>
            <Link
              to="/pricing"
              state={{ fromApp: true }}
              className="btn btn-ghost-dark"
            >
              See pricing &rarr;
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
